import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "utility-theory-cardinal-ordinal-demand-curve-derivation",
  title: "Utility Theory Notes: Cardinal vs Ordinal, Marginal Utility, Law of DMU & Demand Curve Derivation",
  description: "Comprehensive notes on Utility Theory: Cardinal vs Ordinal approaches, Law of Diminishing Marginal Utility, indifference curves, and demand curve derivation.",
  pubDate: "2025-08-21",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["microeconomics", "consumer theory", "utility", "indifference curves"],
  readMinutes: 16,
  heroEyebrow: "Microeconomics Masterclass"
};

export function Body() {
  return (
    <>
      <p>
        Why do consumers buy what they buy? How do they allocate their limited income across a vast array of goods to maximize satisfaction? These are the foundational questions of Consumer Behavior theory. To answer them, economists developed Utility Theory. This article breaks down both the classic Cardinal approach (Alfred Marshall) and the modern Ordinal approach (J.R. Hicks and R.G.D. Allen), explains the laws governing them, derives the demand curve, and includes robust numerical problems.
      </p>

      <h2>1. The Cardinal Utility Approach (Marshallian)</h2>
      <p>
        The Cardinal Utility approach assumes that utility (satisfaction) is a measurable entity. According to Marshall, we can measure utility in absolute quantitative units called "utils".
      </p>
      
      <h3>Key Concepts</h3>
      <ul>
        <li><strong>Total Utility (TU):</strong> The total satisfaction derived from consuming a given quantity of a good.</li>
        <li><strong>Marginal Utility (MU):</strong> The additional satisfaction derived from consuming one more unit of a good. Mathematically, MU = d(TU)/dQ.</li>
      </ul>

      <h3>The Law of Diminishing Marginal Utility (DMU)</h3>
      <p>
        This law states that as a consumer consumes more and more units of a specific commodity, the marginal utility derived from each successive unit goes on diminishing.
      </p>
      <p>
        <strong>Relationship between TU and MU:</strong>
        <br />1. When MU is positive, TU is increasing.
        <br />2. When MU is zero, TU is at its maximum (Point of Satiation).
        <br />3. When MU becomes negative, TU starts declining.
      </p>
      <p>
        <strong>Exceptions to DMU:</strong> Hobbies/Collections (stamp collecting), Money/Wealth (greed), and intoxicants (addiction). However, these are strictly debated in pure theory.
      </p>

      <h3>Consumer Equilibrium (Equi-Marginal Principle)</h3>
      <p>
        A rational consumer aims to maximize total utility subject to their budget constraint.
        <br />For a single good: Equilibrium is achieved when MUx = Px (assuming marginal utility of money is 1).
        <br />For two goods (x and y): Equilibrium is achieved when the ratio of marginal utility to price is equal across goods:
        <br /><strong>MUx / Px = MUy / Py = MUm</strong>
      </p>

      <h3>Derivation of the Demand Curve</h3>
      <p>
        The downward-sloping demand curve is a direct consequence of the Law of DMU. Because successive units yield less utility, a consumer is only willing to buy additional units if the price is lower. Thus, Price must equal Marginal Utility (P = MU). As Q increases, MU falls; therefore, P must fall to induce the consumer to buy more, resulting in a downward-sloping demand curve.
      </p>

      <h2>2. The Ordinal Utility Approach (Hicks-Allen)</h2>
      <p>
        Economists realized that psychological satisfaction cannot be quantified in absolute "utils". Hicks and Allen introduced Ordinal Utility, suggesting consumers can only <em>rank</em> their preferences (1st, 2nd, 3rd) rather than measure them exactly.
      </p>

      <h3>Preference Axioms</h3>
      <ul>
        <li><strong>Completeness:</strong> For any bundles A and B, the consumer either prefers A to B, prefers B to A, or is indifferent between them.</li>
        <li><strong>Transitivity:</strong> If A is preferred to B, and B is preferred to C, then A must be preferred to C.</li>
        <li><strong>Non-satiation (More is better):</strong> A bundle with more of at least one good and no less of any other is strictly preferred.</li>
      </ul>

      <h3>Indifference Curves (IC)</h3>
      <p>
        An Indifference Curve represents all combinations of two goods (X and Y) that yield the exact same level of total satisfaction to the consumer.
      </p>
      <p>
        <strong>Properties of ICs:</strong>
        <br />1. Downward sloping (due to non-satiation).
        <br />2. Convex to the origin (due to diminishing Marginal Rate of Substitution - MRS).
        <br />3. Higher IC represents higher utility.
        <br />4. ICs cannot intersect (violates transitivity).
      </p>

      <h3>Consumer Equilibrium in Ordinal Utility</h3>
      <p>
        Equilibrium occurs where the highest possible Indifference Curve is tangent to the Budget Line.
        <br />Budget Line equation: Px*X + Py*Y = M (Income)
        <br />Tangency Condition: <strong>MRSxy = Px / Py</strong>
        <br />(Since MRSxy = MUx / MUy, this mirrors the equi-marginal principle: MUx / MUy = Px / Py).
      </p>

      <h2>3. Cardinal vs Ordinal Comparison Table</h2>
      <table border={1} cellPadding={5} style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Cardinal Utility (Marshall)</th>
            <th>Ordinal Utility (Hicks-Allen)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Measurability</td>
            <td>Quantitative (Utils)</td>
            <td>Qualitative (Ranking/Ordering)</td>
          </tr>
          <tr>
            <td>Analytical Tool</td>
            <td>Marginal Utility Analysis</td>
            <td>Indifference Curve Analysis</td>
          </tr>
          <tr>
            <td>Marginal Utility of Money</td>
            <td>Assumed constant</td>
            <td>Not assumed constant</td>
          </tr>
          <tr>
            <td>Demand Analysis</td>
            <td>Cannot separate Income & Substitution effects</td>
            <td>Successfully decomposes Income & Substitution effects</td>
          </tr>
        </tbody>
      </table>

      <h2>4. Marshallian vs Hicksian Demand</h2>
      <p>
        <strong>Marshallian Demand (Uncompensated):</strong> Demand derived holding nominal income constant. It incorporates both substitution and income effects.
      </p>
      <p>
        <strong>Hicksian Demand (Compensated):</strong> Demand derived holding <em>utility</em> constant. It isolates the pure substitution effect. The Hicksian demand curve is always downward sloping, even for Giffen goods.
      </p>

      <h2>5. Consumer Surplus</h2>
      <p>
        The difference between what a consumer is willing to pay (based on utility) and what they actually pay (market price). Graphically, it is the area below the demand curve and above the price line.
      </p>

      <h2>6. Worked Numerical Problems</h2>

      <h3>Problem 1: Equi-Marginal Principle</h3>
      <p>
        <strong>Question:</strong> Consumer has M = ₹40. Px = ₹10, Py = ₹5. MU schedules are:
        <br />Q: 1, 2, 3, 4
        <br />MUx: 50, 40, 30, 20
        <br />MUy: 30, 20, 15, 10
        <br />Find optimal consumption.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />Calculate MU/P for each:
        <br />MUx/Px: 50/10=5, 40/10=4, 30/10=3, 20/10=2
        <br />MUy/Py: 30/5=6, 20/5=4, 15/5=3, 10/5=2
        <br />Equate MUx/Px = MUy/Py.
        <br />Possibility A: MUx/Px = MUy/Py = 4. This happens at 2 units of X and 2 units of Y.
        <br />Check Budget: 10(2) + 5(2) = 20 + 10 = 30. (Doesn't exhaust ₹40).
        <br />Possibility B: MUx/Px = MUy/Py = 3. This happens at 3 units of X and 3 units of Y.
        <br />Check Budget: 10(3) + 5(3) = 30 + 15 = 45. (Exceeds ₹40 budget).
        <br />Wait, let's look for integer allocation: Buy 2 units of X (cost 20, MUx/Px=4) and 4 units of Y (cost 20, MUy/Py=2). Here MUx/Px ≠ MUy/Py.
        <br />Let's trace utility step by step:
        <br />Spend first ₹5: Buy Y (MU/P = 6). Total spent = 5.
        <br />Spend next ₹10: Buy X (MU/P = 5). Total spent = 15.
        <br />Spend next ₹5: Buy Y (MU/P = 4). Total spent = 20.
        <br />Spend next ₹10: Buy X (MU/P = 4). Total spent = 30.
        <br />Spend final ₹10: Buy 2 units of Y (MU/P = 3 and 2) or 1 unit of X (MU/P = 3).
        <br />Wait, if we buy 1 X, we get 30 utility for 10 rupees (3 per rupee). If we buy 2 Ys, we get 15+10=25 utility for 10 rupees (2.5 per rupee).
        <br />So, buy X.
        <br />Final bundle: 3 units of X, 2 units of Y. Total expenditure: 3(10) + 2(5) = 40.
        <br />Here, MUx/Px for 3rd unit = 3. MUy/Py for 2nd unit = 4. Marginal conditions are approximate due to lumpiness of goods.
      </p>

      <h3>Problem 2: Deriving Demand Function</h3>
      <p>
        <strong>Question:</strong> Utility function U(X,Y) = X^0.5 * Y^0.5. M = 100, Px = 2, Py = 4. Find X and Y.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />This is a Cobb-Douglas utility function. MUx = 0.5 * (Y/X)^0.5. MUy = 0.5 * (X/Y)^0.5.
        <br />MRSxy = MUx / MUy = Y / X.
        <br />Equilibrium condition: MRS = Px / Py =&gt; Y / X = 2 / 4 = 1/2 =&gt; X = 2Y.
        <br />Substitute into budget constraint: Px*X + Py*Y = M
        <br />2*(2Y) + 4*Y = 100 =&gt; 4Y + 4Y = 100 =&gt; 8Y = 100 =&gt; Y = 12.5.
        <br />X = 2Y = 25.
        <br />Optimal Bundle: X = 25, Y = 12.5.
      </p>

      <h3>Problem 3: Marginal Utility calculation</h3>
      <p>
        <strong>Question:</strong> TU = 100Q - 2Q^2. Find MU and the quantity where satiation is reached.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />MU = d(TU)/dQ = 100 - 4Q.
        <br />Satiation is reached when MU = 0.
        <br />100 - 4Q = 0 =&gt; 4Q = 100 =&gt; Q = 25.
      </p>
      
      <p><strong>Note for exams:</strong> Always state your assumptions clearly, particularly whether you are using Cardinal or Ordinal framework, and clearly label all axes and curves on your graphs.</p>
    </>
  );
}


